import { useEffect, useState } from "react";

import { cn } from "~/lib/utils";

import { useTourContext } from "../context/tour-context";
import type { ElementRect } from "../utils/positioning";
import { getElementRect, resolveTourSelector } from "../utils/positioning";

// AIDEV-NOTE: Tour overlay creates backdrop with spotlight effect on target element
// Uses SVG mask to create transparent "window" over highlighted element

interface TourOverlayProps {
    /** Whether to allow clicking backdrop to close tour */
    allowBackdropClose?: boolean;
}

export function TourOverlay({ allowBackdropClose = true }: TourOverlayProps) {
    const { isActive, currentStep, hasNext, nextStep, endTour } =
        useTourContext();
    const [targetRect, setTargetRect] = useState<ElementRect | null>(null);

    // Find and track the target element
    useEffect(() => {
        if (!isActive || !currentStep) {
            setTargetRect(null);
            return;
        }

        // Find target element by selector or data-tour-id - must be visible
        const selector = resolveTourSelector(currentStep.targetSelector);

        // AIDEV-NOTE: Comma-separated selectors are tried IN ORDER, each exhausted before
        // the next, so a step can express "prefer this target, fall back to that one".
        // querySelectorAll on the whole list returns DOM order instead, which silently
        // defeats the preference whenever the fallback sits earlier in the page. Must match
        // findVisible in packages/config/dpmp-tour.tsx — the overlay highlights one element
        // and the step's own code acts on another if these two disagree.
        const selectorGroups = selector
            .split(",")
            .map((part) => part.trim())
            .filter(Boolean);
        let targetElement: HTMLElement | undefined;
        let allMatches: NodeListOf<Element> = document.querySelectorAll(
            selectorGroups[0] ?? selector,
        );
        for (const group of selectorGroups) {
            const matches = document.querySelectorAll(group);
            const found = Array.from(matches).find(
                (el) => (el as HTMLElement).offsetParent !== null,
            ) as HTMLElement | undefined;
            if (found) {
                allMatches = matches;
                targetElement = found;
                break;
            }
        }

        if (!targetElement) {
            console.warn(
                `[TourOverlay] Target element not found (or not visible): ${currentStep.targetSelector}`,
                {
                    totalMatches: allMatches.length,
                    visibleMatches: Array.from(allMatches).filter(
                        (el) => (el as HTMLElement).offsetParent !== null,
                    ).length,
                },
            );
            setTargetRect(null);
            return;
        }

        // Scroll element into view if not visible
        const elementRect = targetElement.getBoundingClientRect();
        const isInViewport =
            elementRect.top >= 0 &&
            elementRect.left >= 0 &&
            elementRect.bottom <=
                (window.innerHeight || document.documentElement.clientHeight) &&
            elementRect.right <=
                (window.innerWidth || document.documentElement.clientWidth);

        if (!isInViewport) {
            targetElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
            });
        }

        // Apply highlight state if specified
        if (currentStep.highlightState) {
            applyHighlightState(targetElement, currentStep.highlightState);
        }

        // AIDEV-NOTE: End/advance tour when user clicks the highlighted target element
        // This ensures the tour closes when e.g. clicking the Clinical Notes button
        const handleTargetClick = () => {
            if (hasNext) {
                nextStep();
            } else {
                endTour();
            }
        };
        targetElement.addEventListener("click", handleTargetClick);

        // Get element position
        const updateRect = () => {
            const rect = getElementRect(targetElement);
            setTargetRect(rect);
        };

        // Delay initial position calculation to allow scroll to complete
        setTimeout(updateRect, 300);

        // Update position on scroll/resize
        window.addEventListener("scroll", updateRect, true);
        window.addEventListener("resize", updateRect);

        return () => {
            targetElement.removeEventListener("click", handleTargetClick);
            window.removeEventListener("scroll", updateRect, true);
            window.removeEventListener("resize", updateRect);

            // Remove highlight state
            if (currentStep.highlightState) {
                removeHighlightState(targetElement, currentStep.highlightState);
            }
        };
    }, [isActive, currentStep, hasNext, nextStep, endTour]);

    if (!isActive || !currentStep) {
        return null;
    }

    return (
        <>
            {/* Simple border highlight around the target element */}
            {targetRect && (
                <div
                    className="pointer-events-none fixed z-[100] rounded-lg border-4 border-brand-terra-600 shadow-[0_0_20px_8px_rgba(216,90,60,0.5)] transition-all duration-300 tour-pulse-slow"
                    style={{
                        top: targetRect.top - 12,
                        left: targetRect.left - 12,
                        width: targetRect.width + 24,
                        height: targetRect.height + 24,
                    }}
                />
            )}
        </>
    );
}

/** Apply visual state to highlighted element (e.g., hover, expanded) */
function applyHighlightState(
    element: HTMLElement,
    state: "hover" | "expanded" | "active",
): void {
    switch (state) {
        case "hover":
            // Just add class for any visual styling
            element.setAttribute("data-tour-hover", "true");
            element.classList.add("tour-hover-state");
            break;
        case "expanded":
            element.setAttribute("data-tour-expanded", "true");
            // Trigger click to expand if it's a collapsible element
            const trigger = element.querySelector("[data-state]");
            if (trigger instanceof HTMLElement) {
                const currentState = trigger.getAttribute("data-state");
                if (currentState === "closed") {
                    trigger.click();
                }
            }
            break;
        case "active":
            element.setAttribute("data-tour-active", "true");
            element.classList.add("tour-active-state");
            break;
    }
}

/** Remove visual state from element */
function removeHighlightState(
    element: HTMLElement,
    state: "hover" | "expanded" | "active",
): void {
    switch (state) {
        case "hover":
            element.removeAttribute("data-tour-hover");
            element.classList.remove("tour-hover-state");
            break;
        case "expanded":
            element.removeAttribute("data-tour-expanded");
            break;
        case "active":
            element.removeAttribute("data-tour-active");
            element.classList.remove("tour-active-state");
            break;
    }
}
